import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Hexagon, Plus, Calendar, Users, Layers, Activity, Trash2, Copy, Briefcase, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { Pill, Modal, Field, EmptyState } from "./ui-prim";
import { ResetTab } from "./csuite/platinum/EnterprisePanel";
import Logo from "./Logo";
import { statusClass, fK, toast } from "../lib/utils";
import ThemeToggle from "./ThemeToggle";
import { HEALTH_COLORS } from "../lib/accents";
import OnboardingTour from "./OnboardingTour";
import PortfolioOverview from "./PortfolioOverview";
import { useCSuiteAuth } from "../state/CSuiteAuthContext";

export default function ProjectList() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const forceTour = params.get("tour") === "1";
  const { user } = useCSuiteAuth();
  const isAdmin = user?.role === "ADMIN";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [warming, setWarming] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSeed, setNewSeed] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true); setError(""); setWarming(false);
    return api.list()
      .then(d => { setList(d); setLoading(false); setWarming(false); })
      .catch(e => {
        setLoading(false);
        setWarming(false);
        const status = e?.response?.status;
        const msg = e?.response?.data?.detail || e?.message || "Failed to load projects";
        // Hide the scary message for cold-start signals — the auto-retry
        // already exhausted; surface a friendlier "warming up" UI.
        const coldStart = [502, 503, 504, 520, 521, 522, 523, 524].includes(status) || !e?.response;
        setError(coldStart ? "__cold_start__" : msg);
      });
  };
  useEffect(() => {
    load();
    // Listen for in-flight cold-start retries (fired by api.js interceptor)
    // so we can show the friendly "Waking server up..." overlay even
    // before the request finally fails.
    const onWarm = () => setWarming(true);
    window.addEventListener("api:warming", onWarm);
    return () => window.removeEventListener("api:warming", onWarm);
  }, []);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const p = await api.create(newName.trim(), newSeed);
      toast("Project created");
      setShowNew(false); setNewName(""); setNewSeed(true);
      nav(`/projects/${p.id}`);
    } catch (e) { toast("Failed: " + e.message); } finally { setCreating(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await api.remove(id); toast("Deleted"); load();
  };

  const duplicate = async (id) => {
    await api.duplicate(id); toast("Duplicated"); load();
  };

  return (
    <div className="min-h-screen grid-bg">
      <OnboardingTour force={forceTour} />
      {/* Header */}
      <header className="border-b border-[var(--border)] bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => nav("/")} data-testid="btn-cover">
            <Logo height={90} />
            <div className="hidden sm:block">
              <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.18em]">v5 · Operator</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn btn-ghost btn-sm" onClick={() => { try { localStorage.removeItem("harmonizer_tour_seen"); } catch { /* noop */ } nav("/projects?tour=1"); window.location.reload(); }} data-testid="btn-start-tour" title="Replay the welcome tour">
              Tour
            </button>
            {isAdmin && (
              <button className="btn btn-ghost btn-sm" onClick={() => nav("/admin/subscribers")} data-testid="btn-admin-portal" title="Admin control room · subscribers, portfolio, billing">
                <Hexagon size={14}/> <span className="hidden sm:inline">Admin</span>
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => nav("/csuite")} data-testid="btn-csuite-portal" title="C-Suite executive dashboard (CIO/CFO)">
              <Briefcase size={14}/> <span className="hidden sm:inline">C-Suite Portal</span>
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowReset(true)} title="Reset all KPIs to zero" data-testid="btn-reset-all-open">
              <RotateCcw size={13}/> <span className="hidden sm:inline">Reset</span>
            </button>
            <button className="btn btn-primary" onClick={() => setShowNew(true)} data-testid="btn-new-project">
              <Plus size={14}/> New project
            </button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {!loading && !error && list.length > 0 && (
          <PortfolioOverview ownerName={user?.name || user?.email?.split("@")[0] || "there"} />
        )}
        <div className="mb-8 anim-in">
          <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">Workspace</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-zinc-900 mt-1">
            Your projects
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            A precise console for every project: requirements, RAID, gap/fit, budget, stakeholders — orchestrated and AI-assisted.
          </p>
        </div>

        {loading ? (
          warming ? (
            <div className="card p-6 border-amber-200 bg-amber-50" data-testid="project-list-warming">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm bg-amber-100 grid place-items-center shrink-0 text-amber-700">
                  <Activity size={16} className="animate-pulse"/>
                </div>
                <div className="flex-1">
                  <div className="font-display text-base font-bold text-amber-900">Waking the server up…</div>
                  <div className="text-sm text-amber-800 mt-1">The backend goes idle when there's no traffic. This usually takes 20–40 seconds on the first visit. Sit tight — we'll retry automatically.</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card p-8 shimmer">Loading…</div>
          )
        ) : error === "__cold_start__" ? (
          <div className="card p-6 border-amber-200 bg-amber-50" data-testid="project-list-cold-start">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-sm bg-amber-100 grid place-items-center shrink-0 text-amber-700">
                <Activity size={16}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-base font-bold text-amber-900">Server is warming up</div>
                <div className="text-sm text-amber-800 mt-1">The backend was idle and is spinning back up. This usually takes under a minute on the first hit of the day. Try again in a moment.</div>
                <button className="btn btn-primary btn-sm mt-3" onClick={load} data-testid="btn-retry-projects">Try again</button>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="card p-6 border-red-200 bg-red-50" data-testid="project-list-error">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-sm bg-red-100 grid place-items-center shrink-0 text-red-700 font-bold">!</div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-base font-bold text-red-900">Couldn't reach the backend</div>
                <div className="text-sm text-red-800 mt-1 break-words">{error}</div>
                <div className="font-mono text-[10px] text-red-700 mt-3 leading-relaxed">
                  Backend URL: <span className="bg-red-100 px-1.5 py-0.5 rounded-sm">{process.env.REACT_APP_BACKEND_URL || "(not set)"}</span>
                </div>
                <div className="text-xs text-red-700 mt-3">
                  Likely causes: <span className="font-semibold">REACT_APP_BACKEND_URL</span> is wrong, the backend is down, or CORS is blocking the request. Open DevTools → Network tab and check the call to <span className="font-mono">/api/projects</span>.
                </div>
                <button className="btn btn-secondary btn-sm mt-3" onClick={load} data-testid="btn-retry-projects">Retry</button>
              </div>
            </div>
          </div>
        ) : list.length === 0 ? (
          <EmptyState icon={Layers} title="No projects yet" hint="Click 'New project' to start. Choose seeded sample data or an empty workspace." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((p, i) => (
              <article
                key={p.id}
                className="card health-strip hover:border-zinc-300 hover:bg-white/60 transition-all p-5 pl-6 cursor-pointer group anim-in"
                style={{ animationDelay: `${i * 40}ms`, "--health": HEALTH_COLORS[p.health] || "#6366F1" }}
                onClick={() => nav(`/projects/${p.id}`)}
                data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] text-zinc-500 tracking-[0.12em] uppercase truncate">{p.code}</div>
                    <h3 className="font-display text-lg font-bold tracking-tight text-zinc-900 mt-1 truncate">{p.name}</h3>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Pill tone={statusClass(p.health).replace('pill-','')}>{p.health || "—"}</Pill>
                    <Pill tone="neutral">{p.phase || "—"}</Pill>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                  <div className="flex items-center gap-2 text-zinc-500">
                    <Calendar size={12} className="text-zinc-400" />
                    <span>{p.start || "—"} → {p.end || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-500">
                    <Users size={12} className="text-zinc-400" />
                    <span className="truncate">PM: {p.pm || "—"}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-[var(--border)] grid grid-cols-4 gap-3 text-center">
                  {[
                    ["TASKS", p.counts.tasks],
                    ["REQS",  p.counts.requirements],
                    ["RAID",  p.counts.raids],
                    ["ACT",   p.counts.actions],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <div className="font-display text-xl font-bold text-zinc-900">{v}</div>
                      <div className="font-mono text-[9px] text-zinc-500 tracking-[0.18em] mt-0.5">{l}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-[var(--border)] flex items-center justify-between">
                  <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-1">
                    <Activity size={11} /> Budget: <span className="text-zinc-700">{fK(p.budget || 0)}</span>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); duplicate(p.id); }} title="Duplicate" data-testid={`btn-duplicate-${p.id}`}>
                      <Copy size={11}/>
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={async (e) => { e.stopPropagation(); if (window.confirm(`Reset all KPIs and data on "${p.name}"? Non-reversible.`)) { await api.resetProject(p.id); toast("Reset"); load(); } }} title="Reset KPIs to zero" data-testid={`btn-reset-${p.id}`}>
                      <RotateCcw size={11}/>
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); remove(p.id, p.name); }} title="Delete" data-testid={`btn-delete-${p.id}`}>
                      <Trash2 size={11}/>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="Create new project"
        footer={<>
          <button className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={create} disabled={creating || !newName.trim()} data-testid="btn-create-confirm">
            {creating ? "Creating…" : "Create project"}
          </button>
        </>}>
        <Field label="Project name">
          <input className="fld" value={newName} onChange={e => setNewName(e.target.value)}
                 placeholder="e.g. ERP Modernization 2026" autoFocus data-testid="input-new-project-name" />
        </Field>
        <div className="flex items-center justify-between bg-white border border-[var(--border)] rounded-sm p-3">
          <div>
            <div className="text-sm text-zinc-800 font-medium">Seed with sample data</div>
            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">Pre-fill tasks, RAID, requirements, stakeholders</div>
          </div>
          <button
            role="switch"
            aria-checked={newSeed}
            onClick={() => setNewSeed(!newSeed)}
            className={`w-10 h-6 rounded-full p-0.5 transition-colors ${newSeed ? "bg-zinc-50" : "bg-zinc-200"}`}
            data-testid="toggle-seed">
            <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${newSeed ? "translate-x-4" : "translate-x-0"}`} />
          </button>
        </div>
      </Modal>

      <Modal open={showReset} onClose={() => setShowReset(false)} title="Reset workspace"
        footer={<button className="btn btn-ghost" onClick={() => setShowReset(false)}>Close</button>}>
        <ResetTab compact onReset={() => { load(); setTimeout(() => setShowReset(false), 1200); }} />
      </Modal>
    </div>
  );
}
